import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageParts";
import { BookOpen, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function TradingJournal() {
  const { pairs, activeSymbol } = useDashboard();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ symbol: activeSymbol, direction: "LONG", entry: "", exit: "", pnl: "", notes: "" });

  const load = () => api.getJournal().then(setEntries).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { setForm((f) => ({ ...f, symbol: activeSymbol })); }, [activeSymbol]);

  const add = async () => {
    if (!form.notes && !form.entry) { toast.error("Add at least a note or entry"); return; }
    await api.addJournal({
      symbol: form.symbol, direction: form.direction,
      entry: form.entry ? Number(form.entry) : null, exit: form.exit ? Number(form.exit) : null,
      pnl: form.pnl ? Number(form.pnl) : null, notes: form.notes, tags: [],
    });
    toast.success("Journal entry added");
    setForm({ ...form, entry: "", exit: "", pnl: "", notes: "" });
    load();
  };
  const del = async (id) => { await api.delJournal(id); load(); };

  return (
    <div className="ym-fade">
      <PageHeader title="Trading Journal" subtitle="Log trades, reasoning & outcomes"
        right={<span className="pill" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.12)", borderColor: "transparent" }}><BookOpen size={13} /> {entries.length} entries</span>} />

      <div className="ym-panel p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
          <select value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="rounded-lg px-2 py-2 text-[12px] outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
            {pairs.map((p) => <option key={p.id} value={p.symbol}>{p.symbol}</option>)}
          </select>
          <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="rounded-lg px-2 py-2 text-[12px] outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
            <option>LONG</option><option>SHORT</option>
          </select>
          <Inp ph="Entry" v={form.entry} on={(v) => setForm({ ...form, entry: v })} />
          <Inp ph="Exit" v={form.exit} on={(v) => setForm({ ...form, exit: v })} />
          <Inp ph="PnL $" v={form.pnl} on={(v) => setForm({ ...form, pnl: v })} />
          <button onClick={add} className="flex items-center justify-center gap-1.5 rounded-lg text-[12px] font-semibold ym-hover" style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}><Plus size={14} /> Add</button>
        </div>
        <textarea placeholder="Trade reasoning, emotions, lessons…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full rounded-lg px-3 py-2 text-[12px] outline-none resize-none" rows={2} style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
      </div>

      <div className="space-y-2">
        {entries.map((e) => (
          <div key={e.id} className="ym-card p-3.5">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="font-bold text-[13px]">{e.symbol}</span>
              <span className="pill" style={{ color: e.direction === "LONG" ? "var(--green)" : "var(--red)", background: "transparent", borderColor: "var(--border)" }}>{e.direction}</span>
              {e.pnl != null && <span className="tabnum font-bold text-[12px]" style={{ color: e.pnl >= 0 ? "var(--green)" : "var(--red)" }}>${e.pnl}</span>}
              <span className="ml-auto text-[10px]" style={{ color: "var(--text-dim)" }}>{new Date(e.createdAt).toLocaleString()}</span>
              <button onClick={() => del(e.id)} className="grid place-items-center rounded-md ym-hover" style={{ width: 24, height: 24, background: "var(--panel-2)", border: "1px solid var(--border)" }}><Trash2 size={12} style={{ color: "var(--red)" }} /></button>
            </div>
            {(e.entry || e.exit) && <div className="text-[11px] tabnum mb-1" style={{ color: "var(--text-muted)" }}>Entry {e.entry ?? "—"} · Exit {e.exit ?? "—"}</div>}
            {e.notes && <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>{e.notes}</p>}
          </div>
        ))}
        {!entries.length && <div className="ym-card p-8 text-center text-[12px]" style={{ color: "var(--text-dim)" }}>No journal entries yet.</div>}
      </div>
    </div>
  );
}

function Inp({ ph, v, on }) {
  return <input placeholder={ph} value={v} onChange={(e) => on(e.target.value)} className="rounded-lg px-2 py-2 text-[12px] tabnum outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />;
}
